import { createServerFn } from '@tanstack/react-start'
import { notFound } from '@tanstack/react-router'
import { DropifyApiError, rewriteHtmlImagesThroughThumbor, slugifyTag } from './dropify'
import type { BlogTag, StorefrontBlogPost } from './dropify'
import { stripHtml, truncate } from './seo'
import { cleanBlogPostCopy } from './blogCopy'

// Blog content is served by the Whatzap AI-blog API, authenticated with a
// read-only blog key (VITE_BLOG_KEY) passed via the X-Blog-Key header.
const BLOG_API_BASE = (import.meta.env.VITE_BLOG_API_BASE_URL || 'https://whatzap.dropify.id').replace(/\/+$/, '')
const BLOG_KEY = import.meta.env.VITE_BLOG_KEY as string | undefined

if (!BLOG_KEY) {
  console.warn('Missing VITE_BLOG_KEY — blog endpoints will be requested without authentication.')
}

type WhatzapTaxonomy = { id: number; name: string; slug: string; post_count?: number }
type WhatzapAuthor = { id: number; name: string; post_count?: number }

type WhatzapBlogSummary = {
  id: number
  title: string
  slug: string
  status: string
  publish_url: string | null
  published_at: string
  category: WhatzapTaxonomy | null
  tags: WhatzapTaxonomy[]
  author: WhatzapAuthor | null
}

type WhatzapBlogDetail = WhatzapBlogSummary & {
  keyword: string | null
  meta_title: string | null
  meta_description: string | null
  featured_image_url: string | null
  featured_image_alt: string | null
  body_html: string | null
  body_markdown: string
}

export type BlogListParams = {
  limit?: number
  offset?: number
  category?: string
  tag?: string
  q?: string
  author?: string
}

// Small in-memory TTL cache. The SSR server is long-lived, so this de-dupes the
// detail fetches that list-enrichment and the post page both trigger.
const CACHE_TTL_MS = 5 * 60 * 1000
const STALE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000
const BLOG_REQUEST_TIMEOUT_MS = 1_500
const BLOG_CIRCUIT_COOLDOWN_MS = 30 * 1000

type BlogCacheEntry = {
  freshUntil: number
  staleUntil: number
  data?: unknown
  promise?: Promise<unknown>
}

const cache = new Map<string, BlogCacheEntry>()
let blogCircuitOpenUntil = 0

function isTemporaryBlogFailure(error: unknown) {
  return !(error instanceof DropifyApiError) || error.status === 429 || error.status >= 500
}

function recordBlogFailure(error: unknown, startedAt: number) {
  if (!isTemporaryBlogFailure(error)) return
  blogCircuitOpenUntil = Date.now() + BLOG_CIRCUIT_COOLDOWN_MS
  if (import.meta.env.SSR) {
    console.warn(JSON.stringify({
      event: 'blog_api_failed',
      status: error instanceof DropifyApiError ? error.status : null,
      errorClass: error instanceof DropifyApiError ? 'http'
        : error instanceof Error && error.name === 'AbortError' ? 'timeout' : 'network_or_parse',
      elapsedMs: Date.now() - startedAt,
      timestamp: new Date().toISOString(),
      deployment: process.env.DEPLOYMENT_SHA || 'unknown',
    }))
  }
}

async function blogFetch<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  const url = new URL(`${BLOG_API_BASE}${path}`)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
    }
  }

  const cacheKey = url.toString()
  const cached = cache.get(cacheKey)
  const now = Date.now()
  if (cached?.data !== undefined && cached.freshUntil > now) return cached.data as T
  if (cached?.promise) return cached.promise as Promise<T>
  if (blogCircuitOpenUntil > now) {
    if (cached?.data !== undefined && cached.staleUntil > now) return cached.data as T
    throw new DropifyApiError(503, 'Blog API temporarily unavailable')
  }

  const startedAt = Date.now()
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), BLOG_REQUEST_TIMEOUT_MS)
  const request = (async () => {
    try {
      const response = await fetch(url, {
        headers: {
          'X-Blog-Key': BLOG_KEY ?? '',
          Accept: 'application/json',
        },
        signal: controller.signal,
      })
      if (!response.ok) {
        throw new DropifyApiError(response.status, `Blog API ${response.status} ${response.statusText} for ${path}`)
      }
      const data = (await response.json()) as T
      blogCircuitOpenUntil = 0
      cache.set(cacheKey, {
        freshUntil: Date.now() + CACHE_TTL_MS,
        staleUntil: Date.now() + STALE_CACHE_TTL_MS,
        data,
      })
      return data
    } catch (error) {
      recordBlogFailure(error, startedAt)
      if (isTemporaryBlogFailure(error) && cached?.data !== undefined && cached.staleUntil > Date.now()) {
        cache.set(cacheKey, { ...cached, promise: undefined })
        return cached.data as T
      }
      cache.delete(cacheKey)
      throw error
    } finally {
      clearTimeout(timeout)
    }
  })()

  cache.set(cacheKey, {
    freshUntil: cached?.freshUntil ?? 0,
    staleUntil: cached?.staleUntil ?? 0,
    data: cached?.data,
    promise: request,
  })
  return request
}

function firstImageUrl(html: string): string | null {
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i)
  return match ? match[1] : null
}

// The generated body_html opens with an <h1> that repeats the post title. The
// detail page already renders the title as the page's single <h1>, so strip the
// leading heading to avoid a duplicate-H1 SEO regression.
function stripLeadingH1(html: string): string {
  return html.replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>/i, '').trim()
}

function tagList(tags: WhatzapTaxonomy[] | undefined): BlogTag[] {
  return (tags ?? [])
    .filter((tag) => tag.name)
    .map((tag) => ({ name: tag.name, slug: tag.slug || slugifyTag(tag.name) }))
}

export function detailToPost(detail: WhatzapBlogDetail): StorefrontBlogPost {
  const html = detail.body_html ?? ''
  const excerpt = detail.meta_description?.trim() || truncate(stripHtml(html || detail.body_markdown), 200)

  return cleanBlogPostCopy({
    id: String(detail.id),
    slug: detail.slug,
    title: detail.title,
    excerpt,
    content: rewriteHtmlImagesThroughThumbor(stripLeadingH1(html)),
    featured_image_url: detail.featured_image_url?.trim() || firstImageUrl(html),
    featured_image_alt: detail.featured_image_alt?.trim() || null,
    published_at: detail.published_at ?? null,
    tags: tagList(detail.tags),
    meta_title: detail.meta_title ?? null,
    meta_description: detail.meta_description ?? null,
    author: detail.author?.name ?? null,
  })
}

// --- Internal fetchers ---------------------------------------------------
// These run only on the server (they are invoked from the createServerFn
// handlers below). The browser never calls the Whatzap API directly, which
// avoids the cross-origin CORS preflight that intermittently failed on
// client-side navigation, and keeps VITE_BLOG_KEY off the client.

async function fetchBlogPost(slug: string): Promise<StorefrontBlogPost> {
  const detail = await blogFetch<WhatzapBlogDetail>(`/api/v1/blog/posts/${encodeURIComponent(slug)}`)
  return detailToPost(detail)
}

async function listBlogSummaries(params?: BlogListParams): Promise<WhatzapBlogSummary[]> {
  const items = await blogFetch<WhatzapBlogSummary[]>('/api/v1/blog/posts', {
    limit: params?.limit ?? 12,
    offset: params?.offset ?? 0,
    category: params?.category,
    tag: params?.tag,
    q: params?.q,
    author: params?.author,
  })
  return Array.isArray(items) ? items : []
}

// Full posts for cards/lists. The list endpoint summaries are sparse (no body,
// image, or excerpt), so we enrich each one with its cached detail.
async function fetchBlogPosts(params?: BlogListParams): Promise<StorefrontBlogPost[]> {
  const summaries = await listBlogSummaries(params)
  const posts = await Promise.all(
    summaries.map((summary) => fetchBlogPost(summary.slug).catch(() => null)),
  )
  return posts.filter((post): post is StorefrontBlogPost => post !== null)
}

// Lightweight slug + lastmod listing for the sitemap (no per-post detail fetch).
// Walks the offset-paginated endpoint until a short page signals the end.
async function fetchAllBlogPostSlugs(): Promise<Array<{ slug: string; published_at: string | null }>> {
  const PAGE = 100
  const MAX_PAGES = 50
  const all: Array<{ slug: string; published_at: string | null }> = []
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const summaries = await listBlogSummaries({ limit: PAGE, offset: page * PAGE })
    for (const summary of summaries) {
      if (summary.slug) all.push({ slug: summary.slug, published_at: summary.published_at ?? null })
    }
    if (summaries.length < PAGE) break
  }
  return all
}

// --- Server functions ----------------------------------------------------
// Loaders run isomorphically (on the SSR server for a hard refresh, in the
// browser for client-side navigation). Exposing the fetchers as server
// functions forces them to always execute on the server, so the browser only
// makes a same-origin RPC call — no cross-origin request, no CORS preflight.

export const getBlogPost = createServerFn({ method: 'GET' })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data }) => {
    try {
      return await fetchBlogPost(data)
    } catch (error) {
      // A deleted/unknown post should serve a 404, not a 500.
      if (error instanceof DropifyApiError && error.status === 404) throw notFound()
      throw error
    }
  })

export const listBlogPosts = createServerFn({ method: 'GET' })
  .inputValidator((params?: BlogListParams) => params)
  .handler(({ data }) => fetchBlogPosts(data))

export const listAllBlogPostSlugs = createServerFn({ method: 'GET' })
  .handler(() => fetchAllBlogPostSlugs())
